// Formations du personnel (M11), réservées aux RH habilités : catalogue, sessions datées, participants et
// leur état (inscrit, présent, absent, terminé).
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, LienBouton, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, GraduationCap, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerFormation, creerSession, inscrireAgent, INVALIDER_PERSONNEL, modifierParticipation, useAgents, useFormations, useSessionFormation, type Formation, type ParticipantSession } from './api';
import { aujourdhuiIso } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

const ETATS = [{ valeur: 'inscrit', libelle: 'Inscrit' }, { valeur: 'present', libelle: 'Présent' }, { valeur: 'absent', libelle: 'Absent' }, { valeur: 'termine', libelle: 'Terminé' }];

function DialogueFormation({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({ libelle: '', organisme: '', description: '' });
  const action = useAction({ executer: () => creerFormation(v), succes: 'Formation ajoutée au catalogue.', invalider: INVALIDER_PERSONNEL, apres: onFermer });
  return (
    <Dialogue ouvert titre="Nouvelle formation" onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!v.libelle.trim() || !v.organisme.trim()} onClick={() => action.lancer(undefined)}>Ajouter</Bouton></>}>
      <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} required />
      <Champ libelle="Organisme" value={v.organisme} onChange={(e) => setV((x) => ({ ...x, organisme: e.target.value }))} required />
      <Champ libelle="Description" value={v.description} onChange={(e) => setV((x) => ({ ...x, description: e.target.value }))} facultatif />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function DialogueSession({ formation, onFermer }: { formation: Formation; onFermer: () => void }) {
  const [v, setV] = useState({ debut: aujourdhuiIso(), fin: aujourdhuiIso(), lieu: '' });
  const action = useAction({ executer: () => creerSession({ formation_id: formation.id, ...v }), succes: 'Session programmée.', invalider: INVALIDER_PERSONNEL, apres: onFermer });
  return (
    <Dialogue ouvert titre={`Nouvelle session : ${formation.libelle}`} onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!v.lieu.trim()} onClick={() => action.lancer(undefined)}>Programmer</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Début" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} required />
        <Champ libelle="Fin" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} required />
        <Champ libelle="Lieu" value={v.lieu} onChange={(e) => setV((x) => ({ ...x, lieu: e.target.value }))} required />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function Session({ id, modifiable }: { id: string; modifiable: boolean }) {
  const s = useSessionFormation(id);
  const agents = useAgents('', modifiable);
  const [agent, setAgent] = useState('');
  const inscrire = useAction({ executer: () => inscrireAgent(id, { agent_id: agent, etat: 'inscrit' }), succes: 'Agent inscrit.', invalider: INVALIDER_PERSONNEL, apres: () => setAgent('') });
  const modifier = useAction({ executer: (e: { p: string; etat: string }) => modifierParticipation(id, e.p, e.etat), succes: 'Participation mise à jour.', invalider: INVALIDER_PERSONNEL });
  const colonnes: ColonneTableau<ParticipantSession>[] = [
    { id: 'agent', entete: 'Agent', enTeteLigne: true, cellule: (p) => `${p.nom} ${p.prenoms} (${p.matricule})` },
    { id: 'etat', entete: 'État', cellule: (p) => (modifiable
      ? <Selection libelle={`Participation de ${p.nom} ${p.prenoms}`} options={ETATS} value={p.etat} onChange={(e) => modifier.lancer({ p: p.id, etat: e.target.value })} />
      : ETATS.find((x) => x.valeur === p.etat)?.libelle) },
  ];
  return (
    <EtatRequete chargement={s.isPending} erreur={s.error} onReessayer={() => void s.refetch()} lot="L5-A" fonction="La session de formation">
      {s.data ? (
        <div className={commun.pile}>
          {s.data.participants.length ? <Tableau legende="Participants" colonnes={colonnes} lignes={s.data.participants} cleLigne={(p) => p.id} /> : <p>Aucun participant inscrit.</p>}
          {modifier.messageErreur ? <Bandeau type="erreur">{modifier.messageErreur}</Bandeau> : null}
          {modifiable ? (
            <div className={styles.grilleFormulaire}>
              <Selection libelle="Inscrire un agent" options={(agents.data?.elements ?? []).map((a) => ({ valeur: a.id, libelle: `${a.nom} ${a.prenoms} (${a.matricule})` }))} optionVide="Choisir un agent" value={agent} onChange={(e) => setAgent(e.target.value)} />
              <Bouton variante="secondaire" chargement={inscrire.enCours} disabled={!agent} onClick={() => inscrire.lancer(undefined)}>Inscrire</Bouton>
              {inscrire.messageErreur ? <Bandeau type="erreur">{inscrire.messageErreur}</Bandeau> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </EtatRequete>
  );
}

export function PageFormations() {
  const { contexte } = useSession();
  const formations = useFormations();
  const [creation, setCreation] = useState(false);
  const [session, setSession] = useState<Formation | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const modifiable = peut(contexte, 'personnel_sensible', 'modifier');
  return (
    <>
      <EnTetePage etiquette="Personnel" titre="Formations" description="Catalogue des formations, sessions programmées et participation des agents."
        actions={<><LienBouton href="/personnel" icone={ArrowLeft} variante="secondaire">Personnel</LienBouton>{peut(contexte, 'personnel_sensible', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvelle formation</Bouton> : null}</>} />
      <EtatRequete chargement={formations.isPending} erreur={formations.error} onReessayer={() => void formations.refetch()} lot="L5-A" fonction="Les formations">
        {(formations.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucune formation" icone={GraduationCap}><p>Le catalogue est vide.</p></EtatVide> : (
          <div className={commun.pile}>
            {(formations.data?.elements ?? []).map((f) => (
              <Panneau key={f.id} titre={f.libelle} niveau={2} actions={peut(contexte, 'personnel_sensible', 'creer') ? <Bouton variante="secondaire" onClick={() => setSession(f)}>Programmer une session</Bouton> : null}>
                <p>{f.organisme}{f.description ? ` : ${f.description}` : ''}</p>
                {f.sessions.length === 0 ? <p>Aucune session programmée.</p> : f.sessions.map((s) => (
                  <Panneau key={s.id} interne titre={`Du ${date(s.debut)} au ${date(s.fin)}, ${s.lieu} (${s.participants} participant${s.participants > 1 ? 's' : ''})`} niveau={3}
                    actions={<Bouton variante="secondaire" onClick={() => setOuverte(ouverte === s.id ? null : s.id)}>{ouverte === s.id ? 'Masquer' : 'Participants'}</Bouton>}>
                    {ouverte === s.id ? <Session id={s.id} modifiable={modifiable} /> : null}
                  </Panneau>
                ))}
              </Panneau>
            ))}
          </div>
        )}
      </EtatRequete>
      {creation ? <DialogueFormation onFermer={() => setCreation(false)} /> : null}
      {session ? <DialogueSession formation={session} onFermer={() => setSession(null)} /> : null}
    </>
  );
}
